import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Link,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  UserRound,
  Building2,
  Target,
  UserCheck,
  Pencil,
  Phone,
  Mail,
  Linkedin,
  Globe,
  Copy,
  Check,
  CalendarDays,
  Package,
  Flag,
  Megaphone,
  Quote,
} from "lucide-react";
import { dealsAPI } from "../../services/api";

// Lead Profile tab: everything known about the lead (the bulk-import template
// columns plus the Create Lead fields), grouped and editable in place.
// "Requirement: Product/service" is the lead's Category (`interest`).

const SECTIONS = [
  {
    title: "Contact",
    icon: UserRound,
    color: "#6366F1",
    fields: [
      { key: "firstName", label: "First name" },
      { key: "lastName", label: "Last name" },
      { key: "mobile", label: "Phone", readOnly: true, type: "phone", copy: true },
      { key: "alternateNumber", label: "Alternate", type: "phone", copy: true },
      { key: "email", label: "Email", type: "email", copy: true },
      { key: "additionalEmails", label: "Other emails", copy: true },
      { key: "whatsappNumber", label: "WhatsApp", type: "phone", copy: true },
      { key: "linkedinUrl", label: "LinkedIn", type: "url" },
    ],
  },
  {
    title: "Company",
    icon: Building2,
    color: "#0EA5E9",
    fields: [
      { key: "companyName", label: "Company" },
      { key: "website", label: "Website", type: "url" },
      { key: "primaryIndustry", label: "Industry" },
      { key: "department", label: "Department" },
      { key: "jobTitle", label: "Designation" },
      { key: "city", label: "Location" },
      { key: "country", label: "Country" },
    ],
  },
  {
    title: "Requirement & status",
    icon: Target,
    color: "#F59E0B",
    fields: [
      { key: "interest", label: "Product / service" },
      { key: "leadStatus", label: "Status" },
      { key: "importantDate", label: "Important date", type: "date" },
      { key: "remarks", label: "Remarks", multiline: true, full: true },
    ],
  },
  {
    title: "Owner & source",
    icon: UserCheck,
    color: "#10B981",
    fields: [
      { key: "__owner", label: "Lead owner", readOnly: true, hint: "Change it with “Assigned to” at the top of the page" },
      { key: "source", label: "Lead source" },
    ],
  },
];

const EDITABLE_KEYS = SECTIONS.flatMap((s) => s.fields).filter((f) => !f.readOnly).map((f) => f.key);
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const isEmpty = (v) => v === undefined || v === null || String(v).trim() === "";

const toInputDate = (v) => {
  if (!v) return "";
  const d = new Date(v);
  return isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
};

const formatDate = (v) => {
  const d = new Date(v);
  return isNaN(d.getTime()) ? String(v) : `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};

const withProtocol = (url) => (/^https?:\/\//i.test(url) ? url : `https://${url}`);
// "https://www.linkedin.com/in/harsh/" → "linkedin.com/in/harsh"
const prettyUrl = (url) => String(url).replace(/^https?:\/\//i, "").replace(/^www\./i, "").replace(/\/$/, "");
const initials = (deal) =>
  ([deal.firstName, deal.lastName].filter(Boolean).map((n) => n[0]).join("") ||
    String(deal.dealName || deal.name || "?").split(/\s+/).map((n) => n[0]).slice(0, 2).join("")).toUpperCase();

// One line of text that never breaks mid-word; full value on hover
const OneLine = ({ children, title }) => (
  <Box component="span" title={title} sx={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
    {children}
  </Box>
);

const CopyButton = ({ value }) => {
  const [copied, setCopied] = useState(false);
  return (
    <Tooltip title={copied ? "Copied" : "Copy"} placement="top">
      <IconButton
        size="small"
        className="copy-btn"
        onClick={() => {
          navigator.clipboard?.writeText(String(value)).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          });
        }}
        sx={{ p: 0.4, color: copied ? "#16a34a" : "#94a3b8", opacity: copied ? 1 : 0, transition: "opacity .15s" }}
      >
        {copied ? <Check size={13} /> : <Copy size={13} />}
      </IconButton>
    </Tooltip>
  );
};

const renderValue = (field, value) => {
  switch (field.type) {
    case "date":
      return formatDate(value);
    case "email":
      return (
        <OneLine title={value}>
          <Link href={`mailto:${value}`} underline="hover" sx={{ color: "#4f46e5" }}>{value}</Link>
        </OneLine>
      );
    case "url":
      return (
        <OneLine title={value}>
          <Link href={withProtocol(value)} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ color: "#4f46e5" }}>
            {prettyUrl(value)}
          </Link>
        </OneLine>
      );
    default:
      return field.multiline ? String(value) : <OneLine title={String(value)}>{String(value)}</OneLine>;
  }
};

const LeadProfileTab = ({ deal, onPatched, assigneeName }) => {
  const isViewer = localStorage.getItem("userRole") === "viewer";
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const startEdit = () => {
    setForm(
      EDITABLE_KEYS.reduce((acc, key) => {
        acc[key] = key === "importantDate" ? toInputDate(deal[key]) : deal[key] ?? "";
        return acc;
      }, {})
    );
    setError(null);
    setEditing(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      const patch = { ...form, importantDate: form.importantDate || null };
      const res = await dealsAPI.update(deal._id, patch);
      onPatched?.(res.data?.deal || patch);
      setEditing(false);
    } catch (err) {
      setError(err.response?.data?.message || "Couldn't save the lead profile.");
    } finally {
      setSaving(false);
    }
  };

  const valueOf = (key) => (key === "__owner" ? assigneeName : deal[key]);
  const allFields = SECTIONS.flatMap((s) => s.fields);
  const filledCount = allFields.filter((f) => !isEmpty(valueOf(f.key))).length;
  const fullName = [deal.firstName, deal.lastName].filter(Boolean).join(" ") || deal.dealName || deal.name || "Lead";
  const subtitle = [deal.jobTitle, deal.companyName || deal.company?.name].filter(Boolean).join(" · ");

  // Key facts shown as chips in the summary card
  const facts = [
    { icon: Package, label: deal.interest, color: "#7c3aed", bg: "#f5f3ff" },
    { icon: Flag, label: deal.leadStatus, color: "#b45309", bg: "#fffbeb" },
    { icon: Megaphone, label: deal.source, color: "#0369a1", bg: "#f0f9ff" },
    { icon: CalendarDays, label: deal.importantDate && formatDate(deal.importantDate), color: "#be123c", bg: "#fff1f2" },
  ].filter((f) => !isEmpty(f.label));

  const actions = [
    deal.mobile && { icon: Phone, label: "Call", href: `tel:+${String(deal.mobile).replace(/\D/g, "")}` },
    deal.email && { icon: Mail, label: "Email", href: `mailto:${deal.email}` },
    deal.linkedinUrl && { icon: Linkedin, label: "LinkedIn", href: withProtocol(deal.linkedinUrl), external: true },
    deal.website && { icon: Globe, label: "Website", href: withProtocol(deal.website), external: true },
  ].filter(Boolean);

  return (
    // typography: theme font for plain Box text (values), not the browser default serif
    <Box sx={{ typography: "body2" }}>
      {/* ===== Summary ===== */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 2.5 },
          mb: 2,
          borderRadius: 3,
          border: "1px solid #e2e8f0",
          background: "linear-gradient(135deg, #f8faff 0%, #ffffff 60%)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, flexWrap: "wrap" }}>
          <Avatar sx={{ width: 52, height: 52, bgcolor: "#eef2ff", color: "#4f46e5", fontWeight: 700, fontSize: 18 }}>
            {initials(deal)}
          </Avatar>

          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography sx={{ fontSize: 18, fontWeight: 700, color: "#0f172a", lineHeight: 1.3 }}>{fullName}</Typography>
            {subtitle && <Typography sx={{ fontSize: 13.5, color: "#64748b", mt: 0.25 }}>{subtitle}</Typography>}
            {facts.length > 0 && (
              <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mt: 1.25 }}>
                {facts.map(({ icon: Icon, label, color, bg }) => (
                  <Chip
                    key={label}
                    size="small"
                    icon={<Icon size={13} color={color} />}
                    label={label}
                    sx={{ bgcolor: bg, color, fontWeight: 600, fontSize: 12, borderRadius: 1.5, "& .MuiChip-icon": { ml: 0.75 } }}
                  />
                ))}
              </Box>
            )}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            {!editing &&
              actions.map(({ icon: Icon, label, href, external }) => (
                <Tooltip key={label} title={label}>
                  <IconButton
                    component="a"
                    href={href}
                    target={external ? "_blank" : undefined}
                    rel={external ? "noopener noreferrer" : undefined}
                    sx={{ width: 36, height: 36, border: "1px solid #e2e8f0", bgcolor: "#fff", color: "#475569", "&:hover": { color: "#4f46e5", borderColor: "#c7d2fe", bgcolor: "#f5f7ff" } }}
                  >
                    <Icon size={16} />
                  </IconButton>
                </Tooltip>
              ))}
            {!isViewer && !editing && (
              <Button
                variant="contained"
                size="small"
                startIcon={<Pencil size={14} />}
                onClick={startEdit}
                sx={{ ml: 0.5, height: 36, textTransform: "none", fontWeight: 600, borderRadius: 2, boxShadow: "none", bgcolor: "#4f46e5", "&:hover": { bgcolor: "#4338ca", boxShadow: "none" } }}
              >
                Edit profile
              </Button>
            )}
            {editing && (
              <>
                <Button size="small" onClick={() => setEditing(false)} disabled={saving} sx={{ height: 36, textTransform: "none", color: "#64748b" }}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleSave}
                  disabled={saving}
                  sx={{ height: 36, minWidth: 120, textTransform: "none", fontWeight: 600, borderRadius: 2, boxShadow: "none", bgcolor: "#4f46e5", "&:hover": { bgcolor: "#4338ca", boxShadow: "none" } }}
                >
                  {saving ? <CircularProgress size={16} color="inherit" /> : "Save changes"}
                </Button>
              </>
            )}
          </Box>
        </Box>

        {!editing && (
          <Typography sx={{ fontSize: 12, color: "#94a3b8", mt: 1.75 }}>
            {filledCount} of {allFields.length} details on file
            {filledCount < allFields.length && !isViewer && (
              <>
                {" · "}
                <Link component="button" underline="hover" onClick={startEdit} sx={{ fontSize: 12, color: "#4f46e5", verticalAlign: "baseline" }}>
                  add the missing {allFields.length - filledCount}
                </Link>
              </>
            )}
          </Typography>
        )}
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* ===== Sections ===== */}
      <Grid container spacing={2} alignItems="flex-start">
        {SECTIONS.map(({ title, icon: Icon, color, fields }) => {
          const filled = fields.filter((f) => !isEmpty(valueOf(f.key)));
          const remarks = fields.find((f) => f.multiline);
          const rows = editing ? fields : filled.filter((f) => !f.multiline);

          return (
            <Grid item xs={12} md={6} key={title}>
              <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid #e2e8f0", overflow: "hidden" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 2.25, py: 1.5, borderBottom: "1px solid #f1f5f9", bgcolor: "#fcfcfd" }}>
                  <Box sx={{ p: 0.6, borderRadius: 1.5, bgcolor: `${color}14`, color, display: "flex" }}>
                    <Icon size={15} />
                  </Box>
                  <Typography sx={{ fontWeight: 700, fontSize: 13.5, color: "#0f172a" }}>{title}</Typography>
                  {!editing && (
                    <Typography sx={{ ml: "auto", fontSize: 11.5, color: "#94a3b8" }}>
                      {filled.length}/{fields.length}
                    </Typography>
                  )}
                </Box>

                <Box sx={{ px: 2.25, py: editing ? 2 : 0.5 }}>
                  {editing ? (
                    <Grid container spacing={1.5}>
                      {fields.map((field) => (
                        <Grid item xs={12} sm={field.full ? 12 : 6} key={field.key}>
                          {field.readOnly ? (
                            <Box sx={{ px: 1.5, py: 1, borderRadius: 1.5, bgcolor: "#f8fafc", border: "1px dashed #e2e8f0" }}>
                              <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>{field.label}</Typography>
                              <Typography sx={{ fontSize: 14, color: "#334155" }}>{valueOf(field.key) || "—"}</Typography>
                              {field.hint && <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.25 }}>{field.hint}</Typography>}
                            </Box>
                          ) : (
                            <TextField
                              fullWidth
                              size="small"
                              label={field.label}
                              type={field.type === "date" ? "date" : "text"}
                              InputLabelProps={field.type === "date" ? { shrink: true } : undefined}
                              multiline={field.multiline}
                              minRows={field.multiline ? 2 : undefined}
                              value={form[field.key] ?? ""}
                              onChange={(e) => setForm((p) => ({ ...p, [field.key]: e.target.value }))}
                            />
                          )}
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <>
                      {rows.map((field, i) => {
                        const value = valueOf(field.key);
                        return (
                          <Box
                            key={field.key}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                              py: 1.1,
                              borderTop: i ? "1px solid #f1f5f9" : "none",
                              "&:hover .copy-btn": { opacity: 1 },
                            }}
                          >
                            <Typography sx={{ width: 118, flexShrink: 0, fontSize: 13, color: "#64748b" }}>{field.label}</Typography>
                            <Box sx={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 500, color: "#0f172a" }}>
                              {renderValue(field, value)}
                            </Box>
                            {field.copy && <CopyButton value={value} />}
                          </Box>
                        );
                      })}

                      {remarks && !isEmpty(deal[remarks.key]) && (
                        <Box sx={{ display: "flex", gap: 1.25, my: 1.25, p: 1.5, borderRadius: 2, bgcolor: "#f8fafc", borderLeft: "3px solid #fbbf24" }}>
                          <Quote size={15} color="#f59e0b" style={{ flexShrink: 0, marginTop: 2 }} />
                          <Typography sx={{ fontSize: 13.5, color: "#334155", whiteSpace: "pre-wrap", lineHeight: 1.55 }}>
                            {deal[remarks.key]}
                          </Typography>
                        </Box>
                      )}

                      {filled.length === 0 && (
                        <Typography sx={{ py: 2, fontSize: 13, color: "#94a3b8", fontStyle: "italic" }}>No details added yet.</Typography>
                      )}
                    </>
                  )}
                </Box>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default LeadProfileTab;
